import {
  Injectable,
  inject,
  signal,
  computed,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteField,
  query,
  orderBy,
  limit,
  Timestamp,
} from '@angular/fire/firestore';
import {
  Functions,
  httpsCallable,
} from '@angular/fire/functions';
import { AuthService } from './auth.service';
import { describeFirestoreError } from '../utils/firebase-error';
import { AppUser } from '../models/user.model';
import { BranchRole } from '../models/role.model';

/**
 * A membership granted at the moment a user is created, so the account and its
 * standing are one atomic operation. Mirrors InitialMembership in
 * functions/src/clientManagement/types.ts.
 */
export interface InitialMembership {
  scope: 'business' | 'branch';
  businessId: string;
  branchId?: string;
  role: BranchRole;
}

/** Shape returned by the listBusinessUsers callable. */
interface BusinessUserSummary {
  uid: string;
  email: string | null;
  displayName: string | null;
  status: string | null;
}

/**
 * Manages the Firestore `users` collection.
 * Profile-only operations — membership is handled by MembershipService.
 */
@Injectable({ providedIn: 'root' })
export class UserService {
  private firestore = inject(Firestore);
  private functions = inject(Functions);
  private authService = inject(AuthService);
  private injector = inject(Injector);
  private readonly COLLECTION = 'users';

  loading = signal(false);
  error = signal<string | null>(null);
  users = signal<AppUser[]>([]);

  totalUsers = computed(() => this.users().length);
  activeUsers = computed(
    () => this.users().filter((u) => u.status === 'ACTIVE').length,
  );
  disabledUsers = computed(
    () => this.users().filter((u) => u.status === 'DISABLED').length,
  );

  async getUsers(): Promise<AppUser[]> {
    this.loading.set(true);
    this.error.set(null);

    try {
      return await runInInjectionContext(this.injector, async () => {
        const usersRef = collection(this.firestore, this.COLLECTION);
        const q = query(usersRef, orderBy('createdOn', 'desc'), limit(200));
        const snapshot = await getDocs(q);

        const users = snapshot.docs.map(
          (d) => ({ uid: d.id, ...d.data() }) as AppUser,
        );
        this.users.set(users);
        return users;
      });
    } catch (err: unknown) {
      const message =
        describeFirestoreError(err, 'Failed to fetch users');
      this.error.set(message);
      console.error('Failed to fetch users:', err);
      return [];
    } finally {
      this.loading.set(false);
    }
  }

  async getUser(uid: string): Promise<AppUser | null> {
    try {
      return await runInInjectionContext(this.injector, async () => {
        const docRef = doc(this.firestore, this.COLLECTION, uid);
        const snapshot = await getDoc(docRef);
        if (!snapshot.exists()) return null;
        return { uid: snapshot.id, ...snapshot.data() } as AppUser;
      });
    } catch (err: unknown) {
      console.error('Failed to fetch user:', err);
      return null;
    }
  }

  async updateUser(
    uid: string,
    changes: Partial<Pick<AppUser, 'displayName' | 'lastSelectedBusinessId' | 'lastSelectedBranchId'>>,
  ): Promise<boolean> {
    this.loading.set(true);
    this.error.set(null);

    try {
      await runInInjectionContext(this.injector, async () => {
        const docRef = doc(this.firestore, this.COLLECTION, uid);
        const updateData: Record<string, unknown> = {
          updatedOn: Timestamp.now(),
        };
        for (const [key, value] of Object.entries(changes)) {
          if (value === undefined) {
            updateData[key] = deleteField();
          } else {
            updateData[key] = value;
          }
        }
        await updateDoc(docRef, updateData);
      });
      // Deliberately does NOT refresh the users list. getUsers() is a collection
      // query over `users`, which firestore.rules permits only for self or
      // SuperAdmin — and list rules are evaluated per document, so it fails
      // wholesale for everyone else. Since selectBusiness/selectBranch call this
      // method, refreshing here meant a permission-denied on every single
      // context switch for a non-SuperAdmin.
      return true;
    } catch (err: unknown) {
      const message =
        describeFirestoreError(err, 'Failed to update user');
      this.error.set(message);
      console.error('Failed to update user:', err);
      return false;
    } finally {
      this.loading.set(false);
    }
  }

  /** Result of inviteUser: uid and whether the set-password email was sent. */
  inviteResult = signal<{ uid: string; emailSent: boolean } | null>(null);

  /**
   * Create a user and grant their first membership in ONE callable, then trigger
   * Firebase's password-reset email so they can set their own password.
   *
   * `initialMembership` is not optional in practice: creating a user without one
   * requires SuperAdmin, because a user with no standing is invisible to everyone
   * else. Passing it also makes the account and the membership a single atomic
   * operation — the previous two-call shape (createUser, then manageMembership)
   * left an orphaned account whenever the second call failed, and a BUSINESS
   * admin can neither list users nor repair that.
   *
   * Returns the new UID, or null if creation failed.
   */
  async inviteUser(
    email: string,
    displayName: string,
    initialMembership?: InitialMembership,
  ): Promise<string | null> {
    this.loading.set(true);
    this.error.set(null);
    this.inviteResult.set(null);

    try {
      const result = await runInInjectionContext(this.injector, async () => {
        const callable = httpsCallable<
          {
            email: string;
            displayName: string;
            initialMembership?: InitialMembership;
          },
          { uid: string; email: string }
        >(this.functions, 'createUser');
        return await callable({ email, displayName, initialMembership });
      });

      let emailSent = true;
      try {
        await this.authService.resetPassword(email);
      } catch (resetErr: unknown) {
        emailSent = false;
        console.error('Password reset email failed:', resetErr);
        this.error.set(
          'User was created but the set-password email could not be sent. ' +
            'Ask them to use "Forgot password" on the sign-in page.',
        );
      }

      this.inviteResult.set({ uid: result.data.uid, emailSent });
      return result.data.uid;
    } catch (err: unknown) {
      const message =
        describeFirestoreError(err, 'Failed to invite user');
      this.error.set(message);
      console.error('Failed to invite user:', err);
      return null;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Users with standing in one business, via the listBusinessUsers callable.
   *
   * Use this instead of getUsers() anywhere a non-SuperAdmin needs to see names.
   * `users/{uid}` is readable only by self or SuperAdmin, and Firestore evaluates
   * list rules per document, so the collection query fails wholesale for everyone
   * else and silently yields an empty list.
   */
  async getBusinessUsers(businessId: string): Promise<AppUser[]> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const result = await runInInjectionContext(this.injector, async () => {
        const callable = httpsCallable<
          { businessId: string },
          { users: BusinessUserSummary[]; truncated?: boolean }
        >(this.functions, 'listBusinessUsers');
        return await callable({ businessId });
      });

      const users = result.data.users.map(
        (u) =>
          ({
            uid: u.uid,
            email: u.email ?? '',
            displayName: u.displayName ?? '',
            status: u.status ?? 'ACTIVE',
          }) as AppUser,
      );
      this.users.set(users);
      return users;
    } catch (err: unknown) {
      const message =
        describeFirestoreError(err, 'Failed to load business users');
      this.error.set(message);
      console.error('Failed to load business users:', err);
      return [];
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Permanently delete a user from Firebase Authentication and Firestore
   * (all memberships + user doc). SuperAdmin only; callable enforces.
   */
  async deleteUser(uid: string): Promise<boolean> {
    this.loading.set(true);
    this.error.set(null);

    try {
      await runInInjectionContext(this.injector, async () => {
        const callable = httpsCallable<{ targetUid: string }, { success: boolean }>(
          this.functions,
          'deleteUser',
        );
        await callable({ targetUid: uid });
      });
      await this.getUsers();
      return true;
    } catch (err: unknown) {
      const message =
        describeFirestoreError(err, 'Failed to delete user');
      this.error.set(message);
      console.error('Failed to delete user:', err);
      return false;
    } finally {
      this.loading.set(false);
    }
  }

}
